import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Shield, Lock } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import RoleModal from '../../components/platform-admin/RoleModal';
import api from '../../services/api';

interface Permission {
  id: string;
  permission_key: string;
  category: string;
  action: string;
  description?: string;
  is_system: boolean;
}

interface Role {
  id: string;
  role_key: string;
  role_name: string;
  description?: string;
  scope: 'global' | 'tenant' | 'functional' | 'data';
  is_bootstrap: boolean;
  is_active: boolean;
  permissions: string[];
  created_at: string;
}

export const RoleRegistryPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/roles/permissions'),
      ]);
      setRoles(rolesRes.data.data);
      setPermissions(permsRes.data.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm('Are you sure you want to delete this role?')) return;

    try {
      await api.delete(`/roles/${roleId}`);
      setSuccess('Role deleted successfully');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete role');
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'global':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'tenant':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'functional':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'data':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const groupPermissionsByCategory = () => {
    const grouped: Record<string, Permission[]> = {};
    permissions.forEach((perm) => {
      if (!grouped[perm.category]) {
        grouped[perm.category] = [];
      }
      grouped[perm.category].push(perm);
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Master Role Registry
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Manage platform roles and permissions. Changes propagate instantly across all organizations.
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Role
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert
          type="error"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          type="success"
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <Card key={role.id} className="relative">
            {/* Bootstrap Badge */}
            {role.is_bootstrap && (
              <div className="absolute top-4 right-4">
                <Lock className="w-5 h-5 text-amber-500" />
              </div>
            )}

            <div className="space-y-4">
              {/* Role Header */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {role.role_name}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  {role.description || 'No description'}
                </p>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getScopeColor(role.scope)}`}>
                    {role.scope}
                  </span>
                  {!role.is_active && (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Permissions Count */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {role.permissions.length}
                  </span>{' '}
                  {role.permissions.includes('*.*') ? 'permissions (Wildcard)' : 'permissions'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setEditingRole(role)}
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDeleteRole(role.id)}
                  className="flex-1 flex items-center justify-center gap-2"
                  disabled={role.is_bootstrap}
                  title={role.is_bootstrap ? 'System roles cannot be deleted' : undefined}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Permissions Reference */}
      <Card>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Available Permissions
        </h2>
        <div className="space-y-4">
          {Object.entries(groupPermissionsByCategory()).map(([category, perms]) => (
            <div key={category}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 capitalize">
                {category}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {perms.map((perm) => (
                  <div
                    key={perm.id}
                    className="p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                  >
                    <code className="text-sm text-blue-600 dark:text-blue-400">
                      {perm.permission_key}
                    </code>
                    {perm.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {perm.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <RoleModal
        isOpen={showCreateModal || editingRole !== null}
        onClose={() => {
          setShowCreateModal(false);
          setEditingRole(null);
        }}
        onSave={async (roleData) => {
          try {
            if (roleData.id) {
              // Update existing role
              await api.put(`/roles/${roleData.id}`, {
                role_name: roleData.name,
                description: roleData.description,
                permission_ids: roleData.permissionIds,
              });
              setSuccess('Role updated successfully');
            } else {
              // Create new role
              await api.post('/roles', {
                role_name: roleData.name,
                description: roleData.description,
                permission_ids: roleData.permissionIds,
              });
              setSuccess('Role created successfully');
            }
            await fetchData();
            setShowCreateModal(false);
            setEditingRole(null);
          } catch (err: any) {
            throw new Error(err.response?.data?.error || 'Failed to save role');
          }
        }}
        role={editingRole ? {
          id: editingRole.id,
          name: editingRole.role_name,
          description: editingRole.description || '',
          is_bootstrap: editingRole.is_bootstrap,
          permissions: permissions.filter(p => editingRole.permissions.includes(p.permission_key)),
        } : null}
        availablePermissions={permissions}
      />
    </div>
  );
};

// Made with Bob
